/**
 * Doctor Portal — 4-tab dashboard (PDF OPS→LMS §A.5).
 * Tabs: Overview · Availability · Consultations · Clinical Fee
 */
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import {
  QrCode, Copy, Download, Users, TrendingUp, IndianRupee,
  CheckCircle, Calendar, Clock, UserCheck, FileText, Stethoscope,
} from 'lucide-react';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function DoctorPortal() {
  const { user } = useAuth();
  // Nutritionists use the simple chat-only Consultations inbox — the legacy
  // referral/clinical-fee/availability portal is not for them.
  const isNutritionist = user?.medical_category === 'nutritionist';
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isNutritionist) return;
    api.get('/api/doctor/me/dashboard')
      .then(r => setDashboard(r.data))
      .catch(() => toast.error('Could not load dashboard'))
      .finally(() => setLoading(false));
  }, [isNutritionist]);

  if (isNutritionist) return <Navigate to="/doctor/medical-inbox" replace />;
  if (loading) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (!dashboard) return <div className="p-8 text-muted-foreground">No data</div>;

  return (
    <div className="space-y-6" data-testid="doctor-portal">
      <header className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl text-foreground" data-testid="doctor-portal-greeting">
            Welcome, Dr. {dashboard.doctor_name?.replace(/^Dr\.?\s*/i, '')}
            {dashboard.is_verified && (
              <span className="ml-2 inline-flex items-center gap-1 align-middle text-base bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-medium" data-testid="verified-badge">
                ✓ Verified
              </span>
            )}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {dashboard.specialization?.charAt(0).toUpperCase() + dashboard.specialization?.slice(1)} · Soul Sanskar partner
          </p>
        </div>
        {dashboard.next_payout_date && (
          <div className="text-right">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Next payout</p>
            <p className="font-medium">{dashboard.next_payout_date}</p>
          </div>
        )}
      </header>

      {dashboard.ops_status && dashboard.ops_status !== 'ok' && (
        <Card
          className={`p-4 ${
            dashboard.ops_status === 'not_registered'
              ? 'border-rose-200 bg-rose-50'
              : dashboard.ops_status === 'auth_misconfig'
              ? 'border-red-200 bg-red-50'
              : 'border-amber-200 bg-amber-50'
          }`}
          data-testid="doctor-portal-ops-banner"
        >
          <p
            className={`text-xs ${
              dashboard.ops_status === 'not_registered'
                ? 'text-rose-800'
                : dashboard.ops_status === 'auth_misconfig'
                ? 'text-red-800'
                : 'text-amber-800'
            }`}
          >
            {dashboard.ops_status === 'not_registered' ? (
              <>
                <strong>Your doctor profile isn't set up in OPS yet.</strong> Referral stats, payouts, and consultation links will stay blank until OPS completes your onboarding.{' '}
                <a href="mailto:foundersoffice@soulsanskar.com?subject=Set up my doctor profile" className="underline font-medium">
                  Request setup
                </a>
              </>
            ) : dashboard.ops_status === 'auth_misconfig' ? (
              <>OPS returned an auth error — this is a service misconfiguration, please notify the Soul Sanskar team. LMS-owned data below is still live.</>
            ) : (
              <>OPS stats are temporarily unavailable — LMS-owned data (inbox, consultations) is still live.</>
            )}
          </p>
        </Card>
      )}

      <Tabs defaultValue="overview" className="w-full" data-testid="doctor-portal-tabs">
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="availability" data-testid="tab-availability">Availability</TabsTrigger>
          <TabsTrigger value="consultations" data-testid="tab-consultations">Consultations</TabsTrigger>
          <TabsTrigger value="fee" data-testid="tab-clinical-fee">Clinical Fee</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6 space-y-6">
          <OverviewTab dashboard={dashboard} />
        </TabsContent>
        <TabsContent value="availability" className="mt-6">
          <AvailabilityTab />
        </TabsContent>
        <TabsContent value="consultations" className="mt-6">
          <ConsultationsTab />
        </TabsContent>
        <TabsContent value="fee" className="mt-6">
          <ClinicalFeeTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─────────────────────────── Overview Tab ───────────────────────────

function OverviewTab({ dashboard }) {
  const [qr, setQr] = useState(null);
  const [funnelRange, setFunnelRange] = useState('mtd');
  const [funnel, setFunnel] = useState(null);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  useEffect(() => {
    api.get('/api/doctor/me/qr-code').then(r => setQr(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (funnelRange === 'custom' && (!customStart || !customEnd)) return;
    let url = `/api/doctor/me/funnel?range_key=${funnelRange}`;
    if (funnelRange === 'custom') url += `&start=${customStart}&end=${customEnd}`;
    api.get(url).then(r => setFunnel(r.data)).catch(() => setFunnel(null));
  }, [funnelRange, customStart, customEnd]);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const tm = dashboard.this_month || {};
  const all = dashboard.all_time || {};
  const lms = dashboard.lms_stats || {};

  return (
    <div className="space-y-6">
      {/* QR + referral code */}
      <Card className="p-6 bg-gradient-to-br from-teal-50 to-blue-50 border-teal-200" data-testid="doctor-qr-card">
        <div className="flex flex-col md:flex-row gap-6 items-start">
          <div className="shrink-0">
            {qr?.qr_base64 ? (
              <img
                src={`data:image/png;base64,${qr.qr_base64}`}
                alt="Referral QR"
                className="w-44 h-44 rounded-xl bg-white p-2 border border-teal-200"
                data-testid="doctor-qr-image"
              />
            ) : (
              <div className="w-44 h-44 rounded-xl bg-white border border-dashed border-teal-300 flex items-center justify-center">
                <QrCode className="h-10 w-10 text-teal-400" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0 space-y-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-teal-700 font-medium">Referral code</p>
              <div className="flex items-center gap-2 mt-1">
                <code className="text-xl font-mono font-semibold text-teal-900">{dashboard.referral_code || '—'}</code>
                {dashboard.referral_code && (
                  <Button variant="ghost" size="sm" onClick={() => copy(dashboard.referral_code, 'Referral code')} data-testid="copy-referral-code">
                    <Copy className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            {dashboard.referral_link && (
              <div>
                <p className="text-xs uppercase tracking-wide text-teal-700 font-medium">Share link</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-teal-900 truncate">{dashboard.referral_link}</span>
                  <Button variant="ghost" size="sm" onClick={() => copy(dashboard.referral_link, 'Referral link')} data-testid="copy-referral-link">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            <p className="text-xs text-teal-800/80">
              Display this QR at your clinic reception — patients scan, enrol, and get assigned to you automatically.
            </p>
          </div>
        </div>
      </Card>

      {/* This-month stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="New referrals (month)" value={tm.new_referrals ?? '—'} tone="indigo" testId="stat-referrals" />
        <StatCard icon={CheckCircle} label="Conversions (month)" value={tm.enrolled ?? '—'} tone="emerald" testId="stat-conversions" />
        <StatCard icon={IndianRupee} label="Clinical fee (month)" value={tm.commission_earned ? `₹${tm.commission_earned.toLocaleString()}` : '—'} tone="amber" testId="stat-fee-month" />
        <StatCard icon={TrendingUp} label="All-time earnings" value={all.total_earnings ? `₹${all.total_earnings.toLocaleString()}` : '—'} tone="rose" testId="stat-fee-all-time" />
      </div>

      {/* Funnel + Inbox/consults */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-6" data-testid="conversion-funnel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg">Conversion Funnel</h3>
            <div className="flex gap-1" data-testid="funnel-range-tabs">
              {['wtd', 'mtd', 'last_7d', 'last_30d', 'custom'].map(k => (
                <button
                  key={k}
                  onClick={() => setFunnelRange(k)}
                  className={`text-xs px-2 py-1 rounded ${funnelRange === k ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/70'}`}
                  data-testid={`funnel-range-${k}`}
                >{k.toUpperCase().replace('_', ' ')}</button>
              ))}
            </div>
          </div>
          {funnelRange === 'custom' && (
            <div className="flex gap-2 mb-4">
              <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="text-xs border rounded px-2 py-1" data-testid="funnel-start-date" />
              <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="text-xs border rounded px-2 py-1" data-testid="funnel-end-date" />
            </div>
          )}
          {funnel?.ops_status === 'not_registered' ? (
            <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid="funnel-not-registered-banner">
              Funnel will populate once your doctor profile is set up in OPS.
            </p>
          ) : funnel?.ops_status === 'auth_misconfig' ? (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
              OPS auth misconfiguration — please notify the Soul Sanskar team.
            </p>
          ) : funnel?.ops_status === 'unreachable' ? (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              Funnel data not yet synced from OPS — please refresh in a moment.
            </p>
          ) : funnel ? (
            <>
              <FunnelBar label="Total Referred" value={funnel.funnel?.referred ?? 0} max={funnel.funnel?.referred || 1} />
              <FunnelBar label="Paid Trials" value={funnel.funnel?.paid_trials ?? 0} max={funnel.funnel?.referred || 1} />
              <FunnelBar label="Trial Attended" value={funnel.funnel?.trial_attended ?? 0} max={funnel.funnel?.referred || 1} />
              <FunnelBar label="Consultations" value={funnel.funnel?.consultations ?? 0} max={funnel.funnel?.referred || 1} />
              <FunnelBar label="Enrolled" value={funnel.funnel?.enrolled ?? 0} max={funnel.funnel?.referred || 1} />
              <Separator className="my-3" />
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="p-2 bg-amber-50 rounded">
                  <p className="text-muted-foreground">Pending fee</p>
                  <p className="font-semibold">₹{(funnel.clinical_fee?.pending_inr || 0).toLocaleString()}</p>
                </div>
                <div className="p-2 bg-indigo-50 rounded">
                  <p className="text-muted-foreground">Approved</p>
                  <p className="font-semibold">₹{(funnel.clinical_fee?.approved_inr || 0).toLocaleString()}</p>
                </div>
                <div className="p-2 bg-emerald-50 rounded">
                  <p className="text-muted-foreground">Paid</p>
                  <p className="font-semibold">₹{(funnel.clinical_fee?.paid_inr || 0).toLocaleString()}</p>
                </div>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">Loading funnel…</p>
          )}
        </Card>

        <Card className="p-6" data-testid="lms-stats-panel">
          <h3 className="font-heading text-lg mb-4">Inbox & Consultations</h3>
          <RowStat icon={Stethoscope} label="Pending relays" value={lms.inbox_pending ?? 0} accent="text-amber-600" />
          <RowStat icon={UserCheck} label="Replied relays" value={lms.inbox_replied ?? 0} accent="text-emerald-600" />
          <RowStat icon={Calendar} label="Upcoming consults" value={lms.consultations_upcoming ?? 0} />
          <RowStat icon={CheckCircle} label="Completed (month)" value={lms.consultations_this_month ?? 0} />
          <p className="text-xs text-muted-foreground mt-4 border-t pt-3">
            Clinical Fee Rate: <strong>{dashboard.commission_percentage}%</strong> per enrolment
          </p>
        </Card>
      </div>

      {/* Recent referrals */}
      {(dashboard.recent_referrals || []).length > 0 && (
        <Card className="p-6" data-testid="recent-referrals">
          <h3 className="font-heading text-lg mb-4">Recent Referrals</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b">
                <tr>
                  <th className="text-left py-2 font-medium">Patient</th>
                  <th className="text-left py-2 font-medium">Date</th>
                  <th className="text-left py-2 font-medium">Stage</th>
                  <th className="text-right py-2 font-medium">Fee</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.recent_referrals.slice(0, 10).map((r, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-2">{r.patient_name}</td>
                    <td className="py-2 text-muted-foreground">{r.date}</td>
                    <td className="py-2"><Badge variant="secondary">{r.stage}</Badge></td>
                    <td className="py-2 text-right">{r.fee ? `₹${r.fee}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone, testId }) {
  const tones = {
    indigo: 'bg-indigo-50 border-indigo-100 text-indigo-900',
    emerald: 'bg-emerald-50 border-emerald-100 text-emerald-900',
    amber: 'bg-amber-50 border-amber-100 text-amber-900',
    rose: 'bg-rose-50 border-rose-100 text-rose-900',
  };
  return (
    <Card className={`p-4 ${tones[tone] || ''}`} data-testid={testId}>
      <Icon className="h-5 w-5 opacity-60 mb-2" />
      <p className="text-xs uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-xl font-heading font-semibold mt-1">{value}</p>
    </Card>
  );
}

function FunnelBar({ label, value, max }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1">
        <span>{label}</span>
        <span className="font-medium">{value}</span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-gradient-to-r from-teal-500 to-blue-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function RowStat({ icon: Icon, label, value, accent = 'text-foreground' }) {
  return (
    <div className="flex items-center justify-between py-2 border-b last:border-0">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm">{label}</span>
      </div>
      <span className={`font-medium ${accent}`}>{value}</span>
    </div>
  );
}

// ─────────────────────────── Availability Tab ───────────────────────────

function AvailabilityTab() {
  const [schedule, setSchedule] = useState(
    Array.from({ length: 7 }, (_, i) => ({ day_of_week: i, enabled: false, slots: [] }))
  );

  useEffect(() => {
    api.get('/api/doctor/me/availability').then(r => {
      const saved = r.data?.schedule || [];
      if (saved.length === 7) setSchedule(saved);
      else {
        const merged = Array.from({ length: 7 }, (_, i) => {
          const found = saved.find(s => s.day_of_week === i);
          return found || { day_of_week: i, enabled: false, slots: [] };
        });
        setSchedule(merged);
      }
    }).catch(() => {});
  }, []);

  const toggleDay = (i) => {
    const next = [...schedule];
    next[i].enabled = !next[i].enabled;
    if (next[i].enabled && next[i].slots.length === 0) {
      next[i].slots.push({ start: '10:00', end: '13:00' });
    }
    setSchedule(next);
  };

  const addSlot = (i) => {
    const next = [...schedule];
    next[i].slots.push({ start: '14:00', end: '17:00' });
    setSchedule(next);
  };

  const delSlot = (i, j) => {
    const next = [...schedule];
    next[i].slots.splice(j, 1);
    setSchedule(next);
  };

  const updateSlot = (i, j, field, val) => {
    const next = [...schedule];
    next[i].slots[j][field] = val;
    setSchedule(next);
  };

  const save = async () => {
    try {
      await api.put('/api/doctor/me/availability', { schedule });
      toast.success('Availability saved');
    } catch {
      toast.error('Could not save');
    }
  };

  return (
    <Card className="p-6" data-testid="availability-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading text-lg">Weekly schedule</h3>
          <p className="text-xs text-muted-foreground">
            Times shown in clinic timezone (Asia/Kolkata)
          </p>
        </div>
        <Button onClick={save} data-testid="save-availability">Save Availability</Button>
      </div>
      <div className="space-y-3">
        {schedule.map((day, i) => (
          <div key={i} className="border rounded-lg p-3" data-testid={`availability-day-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <Switch checked={day.enabled} onCheckedChange={() => toggleDay(i)} data-testid={`day-toggle-${i}`} />
                <span className="font-medium w-12">{DAYS[i]}</span>
                {!day.enabled && <span className="text-xs text-muted-foreground">Off</span>}
              </div>
              {day.enabled && (
                <Button variant="ghost" size="sm" onClick={() => addSlot(i)} data-testid={`add-slot-${i}`}>
                  + Add Slot
                </Button>
              )}
            </div>
            {day.enabled && day.slots.map((slot, j) => (
              <div key={j} className="flex items-center gap-2 pl-12 mt-2">
                <Input type="time" value={slot.start} onChange={e => updateSlot(i, j, 'start', e.target.value)} className="w-28" />
                <span className="text-muted-foreground">to</span>
                <Input type="time" value={slot.end} onChange={e => updateSlot(i, j, 'end', e.target.value)} className="w-28" />
                <Button variant="ghost" size="sm" onClick={() => delSlot(i, j)} data-testid={`delete-slot-${i}-${j}`}>×</Button>
              </div>
            ))}
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─────────────────────────── Consultations Tab ───────────────────────────

function ConsultationsTab() {
  const [filter, setFilter] = useState('all');
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get(`/api/doctor/me/consultations?filter=${filter}`)
      .then(r => setItems(r.data?.consultations || []))
      .catch(() => setItems([]));
  }, [filter]);

  return (
    <Card className="p-6" data-testid="consultations-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg">Consultations</h3>
        <div className="flex gap-2">
          {['upcoming', 'past', 'all'].map(f => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              data-testid={`consults-filter-${f}`}
            >
              {f[0].toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">No consultations yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="consultations-table">
            <thead className="text-xs text-muted-foreground border-b">
              <tr>
                <th className="text-left py-2">Patient</th>
                <th className="text-left py-2">Status</th>
                <th className="text-left py-2">Created</th>
                <th className="text-right py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(c => (
                <tr key={c._id} className="border-b last:border-0" data-testid={`consult-row-${c._id}`}>
                  <td className="py-2">{c.learner_name || c.learner_email}</td>
                  <td className="py-2"><Badge variant="secondary">{c.status}</Badge></td>
                  <td className="py-2 text-muted-foreground">{c.created_at?.slice(0, 10)}</td>
                  <td className="py-2 text-right">
                    {c.status === 'active' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          await api.post(`/api/doctor/me/consultations/${c._id}/mark-completed`, {});
                          setItems(items.map(x => x._id === c._id ? { ...x, status: 'completed' } : x));
                          toast.success('Marked completed');
                        }}
                      >
                        Complete
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ─────────────────────────── Clinical Fee Tab ───────────────────────────

function ClinicalFeeTab() {
  const [fee, setFee] = useState(null);

  useEffect(() => {
    api.get('/api/doctor/me/clinical-fee').then(r => setFee(r.data)).catch(() => setFee({}));
  }, []);

  if (!fee) return <div className="p-4 text-muted-foreground">Loading…</div>;

  const download = async () => {
    try {
      const resp = await api.get('/api/doctor/me/clinical-fee/statement.pdf', { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clinical-fee-${new Date().toISOString().slice(0, 7)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download statement');
    }
  };

  return (
    <div className="space-y-6" data-testid="clinical-fee-tab">
      <div className="grid grid-cols-3 gap-4">
        <StatCard icon={Clock} label="Pending" value={`₹${(fee.pending_inr || 0).toLocaleString()}`} tone="amber" testId="fee-pending" />
        <StatCard icon={CheckCircle} label="Approved" value={`₹${(fee.approved_inr || 0).toLocaleString()}`} tone="indigo" testId="fee-approved" />
        <StatCard icon={IndianRupee} label="Paid" value={`₹${(fee.paid_inr || 0).toLocaleString()}`} tone="emerald" testId="fee-paid" />
      </div>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg">Fee records</h3>
          <Button onClick={download} variant="outline" size="sm" data-testid="download-statement">
            <Download className="h-4 w-4 mr-2" /> Download statement (PDF)
          </Button>
        </div>
        {(fee.records || []).length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">No fee records yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b">
                <tr>
                  <th className="text-left py-2">Date</th>
                  <th className="text-left py-2">Patient</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-right py-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {fee.records.map((r, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-2 text-muted-foreground">{r.date}</td>
                    <td className="py-2">{r.patient_name}</td>
                    <td className="py-2"><Badge variant="secondary">{r.status}</Badge></td>
                    <td className="py-2 text-right">₹{(r.amount || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Separator className="my-4" />
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <FileText className="h-3 w-3" />
          Fees calculated on base amount (excl. 18% GST). Paid monthly on 15th.
        </p>
      </Card>
    </div>
  );
}
